import { Capacitor } from '@capacitor/core';
import { CofreBiometrico, type Tipo } from 'cofre-biometrico';
import { api } from '@/utils/api';

/**
 * A ENTRADA COM FACE ID OU DIGITAL (1.4; `docs/PLANO-1.4.md`). Opcional, para qualquer papel, só no aplicativo.
 *
 * - Depois de um login COM SENHA, o aplicativo pergunta UMA vez por conta neste aparelho (`OfertaDeBiometria`).
 * - Sim → o servidor emite a CREDENCIAL DO APARELHO (`backend/src/services/credencialDoAparelho.ts`, 90 dias), que vai
 *   para o cofre (`plugins/cofre-biometrico`) — nunca a senha. Fica no aparelho só a marca de que há credencial
 *   (`biometria:conta`: usuário e nome, para o botão da tela de login); o segredo, só no cofre.
 * - Entrar → a biometria abre o cofre → o servidor troca a credencial por uma sessão. Sempre há a volta à senha.
 * - Biometria do aparelho mudou, credencial vencida ou senha trocada → o cofre é apagado e a pessoa entra com a senha.
 * - Sair da conta apaga tudo (`desligarBiometria`, chamada pelo `logout`).
 */
const CHAVE_DO_COFRE = 'credencial-do-aparelho';
const MARCA = 'biometria:conta';
const PERGUNTADO = (userId: number) => `biometria:perguntado:${userId}`;
export const OFERECER_AGORA = 'biometria:oferecer';

export type ContaComBiometria = { userId: number; username: string; nome: string; tipo: Tipo | null };

const ler = <T,>(k: string): T | null => { try { const v = localStorage.getItem(k); return v ? (JSON.parse(v) as T) : null; } catch { return null; } };

export const nomeDaBiometria = (tipo: Tipo | null | undefined) => (tipo === 'face' ? (Capacitor.getPlatform() === 'ios' ? 'Face ID' : 'reconhecimento facial') : tipo === 'digital' ? 'digital' : 'biometria');

export function contaComBiometria(): ContaComBiometria | null {
  return Capacitor.isNativePlatform() ? ler<ContaComBiometria>(MARCA) : null;
}

/** Vale a pena perguntar a esta conta, neste aparelho? (só no app, com biometria disponível, uma vez por conta) */
export async function deveOferecer(userId: number): Promise<Tipo | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try { if (localStorage.getItem(PERGUNTADO(userId))) return null; } catch { return null; }
  const d = await CofreBiometrico.disponivel().catch(() => ({ disponivel: false, tipo: null }));
  return d.disponivel ? (d.tipo ?? 'biometria') : null;
}
export function marcarPerguntado(userId: number) { try { localStorage.setItem(PERGUNTADO(userId), new Date().toISOString()); } catch { /* pergunta de novo na próxima */ } }

export async function ligarBiometria(conta: { id: number; username: string; name: string }, tipo: Tipo | null): Promise<void> {
  const { data } = await api.post<{ credencial: string }>('/api/auth/credencial-do-aparelho');
  await CofreBiometrico.guardar({ chave: CHAVE_DO_COFRE, valor: data.credencial });
  try { localStorage.setItem(MARCA, JSON.stringify({ userId: conta.id, username: conta.username, nome: conta.name, tipo } satisfies ContaComBiometria)); } catch { /* sem a marca, o botão não aparece */ }
}

export async function desligarBiometria(): Promise<void> {
  try { localStorage.removeItem(MARCA); } catch { /* nada */ }
  await CofreBiometrico.apagar({ chave: CHAVE_DO_COFRE }).catch(() => undefined);
}

/** Entrar pela biometria. Devolve a sessão (a mesma forma do login), `null` se a pessoa cancelou ou quer a senha, ou lança com a frase. */
export async function entrarComBiometria(conta: ContaComBiometria): Promise<{ token: string; user: never } | null> {
  let credencial: string;
  try {
    credencial = (await CofreBiometrico.ler({ chave: CHAVE_DO_COFRE, titulo: `Entrar como ${conta.nome}`, botaoSenha: 'Usar a senha' })).valor;
  } catch (e) {
    const code = (e as { code?: string }).code;
    if (code === 'CANCELADO' || code === 'USAR_SENHA') return null;
    if (code === 'BIOMETRIA_MUDOU' || code === 'SEM_SEGREDO') { await desligarBiometria(); throw new Error('A biometria deste aparelho mudou. Entre com a senha (depois você pode ligar de novo).'); }
    throw new Error('Não foi possível usar a biometria agora. Entre com a senha.');
  }
  try {
    const { data } = await api.post('/api/auth/entrar-com-aparelho', { credencial });
    return data;
  } catch (e) {
    const status = (e as { response?: { status?: number } }).response?.status;
    if (status === 401) { await desligarBiometria(); throw new Error('O acesso guardado neste aparelho não vale mais (a senha mudou ou passaram 90 dias). Entre com a senha.'); }
    throw new Error('Sem conexão com o portal agora. Tente de novo.');
  }
}
